import React, { useState, useMemo } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useTroncRecords } from "../../../hooks/useTroncRecords";
import { useEmployees } from "../../../hooks/useEmployees";
import { useLocationContext } from "../../../contexts/LocationContext";

/**
 * Payroll Export tab — generate payroll-ready TRONC data for download.
 */
const PayrollExportTab = ({ shouldFetchData }) => {
  const { currentLocation } = useLocationContext();
  const { records: troncRecords, loading: loadingTronc } = useTroncRecords({ autoFetch: shouldFetchData });
  const { employees, loading: loadingEmployees } = useEmployees({ autoFetch: shouldFetchData, activeOnly: true, troncEligibleOnly: true });

  const [dateRange, setDateRange] = useState("thisMonth");
  const [exportFormat, setExportFormat] = useState("csv");

  const loading = loadingTronc || loadingEmployees;

  const getDateBounds = () => {
    const now = new Date();
    let from = new Date();
    switch (dateRange) {
      case "7days": from.setDate(now.getDate() - 7); return { from, to: now };
 case "30days": from.setDate(now.getDate() - 30); return { from, to: now };
      case "thisMonth": return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
      case "lastMonth":
        return {
      from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          to: new Date(now.getFullYear(), now.getMonth(), 0),
        };
      default: from.setDate(now.getDate() - 30); return { from, to: now };
  }
  };

  const distributed = useMemo(() => {
    let list = (troncRecords || []).filter(
      (r) => r.isDistributed || r.status === "Distributed" || r.status === 3,
    );
    if (currentLocation) {
  list = list.filter((r) => r.locationId === currentLocation.id);
    }
 const { from, to } = getDateBounds();
    list = list.filter((r) => {
   const d = new Date(r.periodStart);
      if (dateRange === "lastMonth") {
  const end = new Date(new Date().getFullYear(), new Date().getMonth(), 0);
        const start = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
      return d >= start && d <= end;
      }
      return d >= from && d <= to;
    });
    return list;
  }, [troncRecords, currentLocation, dateRange]);

  // Build per-employee payroll rows from distributed records
  const payrollRows = useMemo(() => {
    const map = {};
    distributed.forEach((r) => {
      // Each tronc record may have allocations embedded, otherwise aggregate by employee count
      // We'll build a summary row per employee from the distributed data
      const locName = r.locationName || "Unknown";
      const empCount = r.employeeCount || 1;
      const perEmp = empCount > 0 ? (r.amount || 0) / empCount : 0;

      // If we have allocation details, use them; otherwise create a location-level summary
      if (!map[locName]) map[locName] = { location: locName, total: 0, distributions: 0, employeeCount: new Set() };
map[locName].total += r.amount || 0;
      map[locName].distributions += 1;
    });

    // Build employee-level summary from employee list + total
    const empMap = {};
    (employees || []).forEach((e) => {
      if (!e.isEligibleForTronc) return;
      if (currentLocation && e.locationId !== currentLocation.id) return;
      empMap[e.id] = {
   id: e.id,
        payrollNumber: e.payrollNumber || "—",
        name: `${e.firstName} ${e.lastName}`,
jobTitle: e.jobTitle || e.department || "—",
        location: e.locationName || "—",
  niNumber: e.nationalInsuranceNumber ? `***${e.nationalInsuranceNumber.slice(-4)}` : "—",
        troncAmount: 0,
      };
    });

  // Spread distribution totals proportionally (simplified — in production this comes from allocation API)
  const totalAmount = distributed.reduce((s, r) => s + (r.amount || 0), 0);
    const eligibleCount = Object.keys(empMap).length;
    if (eligibleCount > 0 && totalAmount > 0) {
      const perEmp = totalAmount / eligibleCount;
      Object.values(empMap).forEach((e) => { e.troncAmount = perEmp; });
    }

    return Object.values(empMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [distributed, employees, currentLocation]);

  const totalPayroll = useMemo(() => payrollRows.reduce((s, r) => s + r.troncAmount, 0), [payrollRows]);

  const handleExport = () => {
    if (payrollRows.length === 0) return;
    const { from, to } = getDateBounds();
    const periodLabel = `${from.toLocaleDateString("en-GB")}-${to.toLocaleDateString("en-GB")}`;

    const headers = ["Payroll No", "Employee Name", "NI Number", "Job Title", "Location", "TRONC Amount (£)"];
    const rows = payrollRows.map((r) => [r.payrollNumber, r.name, r.niNumber, r.jobTitle, r.location, r.troncAmount.toFixed(2)]);
    // Add totals row
  rows.push(["", "", "", "", "TOTAL", totalPayroll.toFixed(2)]);

    const csv = [
      [`# TRONC Payroll Export — Period: ${periodLabel}`],
      [`# Generated: ${new Date().toLocaleString("en-GB")}`],
      [],
 headers,
      ...rows,
    ].map((r) => r.join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tronc-payroll-export-${periodLabel.replace(/\//g, "-")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const dateRangeOptions = [
    { id: "7days", label: "Last 7 Days" },
 { id: "30days", label: "Last 30 Days" },
    { id: "thisMonth", label: "This Month" },
    { id: "lastMonth", label: "Last Month" },
  ];

  return (
 <div className="space-y-4">
      {/* Controls */}
      <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div className="flex flex-wrap gap-2">
          {dateRangeOptions.map((opt) => (
  <button
    key={opt.id}
        onClick={() => setDateRange(opt.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-base ${dateRange === opt.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
  >
              <Icon name="Calendar" size={14} />
    {opt.label}
            </button>
  ))}
</div>
        <Button variant="primary" size="sm" iconName="Download" iconPosition="left" onClick={handleExport} disabled={payrollRows.length === 0}>
  Export Payroll CSV
        </Button>
    </div>

      {/* Info banner */}
      <div className="card p-4 flex items-start gap-3 border-l-4 border-primary">
     <Icon name="Info" size={18} className="text-primary shrink-0 mt-0.5" />
        <div>
  <p className="text-sm font-semibold text-foreground">Payroll Export</p>
  <p className="text-xs text-muted-foreground">
          This report generates a CSV file suitable for import into your payroll system.
            It includes TRONC amounts per employee for the selected period.
          NI numbers are partially masked for security.
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4 text-center">
    <p className="text-xs text-muted-foreground mb-1">Total TRONC</p>
     <p className="text-xl font-bold text-foreground">£{totalPayroll.toFixed(2)}</p>
        </div>
        <div className="card p-4 text-center">
  <p className="text-xs text-muted-foreground mb-1">Employees</p>
        <p className="text-xl font-bold text-foreground">{payrollRows.length}</p>
      </div>
        <div className="card p-4 text-center">
    <p className="text-xs text-muted-foreground mb-1">Distributions</p>
    <p className="text-xl font-bold text-foreground">{distributed.length}</p>
    </div>
     <div className="card p-4 text-center">
    <p className="text-xs text-muted-foreground mb-1">Avg per Employee</p>
 <p className="text-xl font-bold text-foreground">
£{payrollRows.length > 0 ? (totalPayroll / payrollRows.length).toFixed(2) : "0.00"}
   </p>
        </div>
      </div>

  {/* Table */}
      {loading ? (
   <div className="card p-8 text-center"><div className="spinner mx-auto mb-4" /><p className="text-muted-foreground">Loading payroll data...</p></div>
      ) : payrollRows.length === 0 ? (
 <div className="card p-8 text-center">
    <div className="empty-state-icon mx-auto mb-4"><Icon name="FileSpreadsheet" size={32} /></div>
     <h3 className="text-lg font-semibold mb-2">No payroll data</h3>
  <p className="text-muted-foreground">There are no distributed TRONC records for the selected period.</p>
        </div>
      ) : (
  <div className="card overflow-hidden">
          <div className="overflow-x-auto">
     <table className="w-full">
  <thead className="bg-muted/50">
 <tr>
     <th className="text-left px-5 py-3 text-xs font-semibold text-foreground">Payroll No</th>
      <th className="text-left px-5 py-3 text-xs font-semibold text-foreground">Employee</th>
  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden sm:table-cell">NI Number</th>
       <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden md:table-cell">Job Title</th>
      <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden lg:table-cell">Location</th>
<th className="text-right px-5 py-3 text-xs font-semibold text-foreground">TRONC (£)</th>
         </tr>
  </thead>
   <tbody className="divide-y divide-border">
         {payrollRows.map((row) => (
    <tr key={row.id} className="hover:bg-muted/30 transition-base">
     <td className="px-5 py-3 text-sm text-muted-foreground font-mono">{row.payrollNumber}</td>
       <td className="px-5 py-3 text-sm font-semibold text-foreground">{row.name}</td>
   <td className="px-5 py-3 text-sm text-muted-foreground font-mono hidden sm:table-cell">{row.niNumber}</td>
      <td className="px-5 py-3 text-sm text-muted-foreground hidden md:table-cell">{row.jobTitle}</td>
     <td className="px-5 py-3 text-sm text-muted-foreground hidden lg:table-cell">{row.location}</td>
  <td className="px-5 py-3 text-sm font-bold text-foreground text-right">£{row.troncAmount.toFixed(2)}</td>
  </tr>
           ))}
</tbody>
  <tfoot className="bg-muted/50 border-t-2 border-border">
            <tr>
  <td colSpan={5} className="px-5 py-3 text-sm font-bold text-foreground text-right">Total</td>
    <td className="px-5 py-3 text-sm font-bold text-primary text-right">£{totalPayroll.toFixed(2)}</td>
     </tr>
              </tfoot>
    </table>
   </div>
        </div>
      )}
    </div>
);
};

export default PayrollExportTab;
